// URL DA PÁGINA NA MENSAGEM DO WHATSAPP — rollout em 2 fases.
//
// Regra do recurso: TODO botão de WhatsApp do site leva, no fim da mensagem, a URL exata
// da página em que o visitante estava — INCLUINDO a querystring de filtros
// (ex.: /imoveis/venda/-/-/-/-?filtros&min=300000&max=500000). Sem isso o corretor recebe
// o contato sem saber de onde ele veio.
//
// FASE 1 (agora): allowlist por codsite — 2463 (quem pediu) + 1722 (tenant PILOTO de teste).
//   Os ~300 sites rodam este mesmo código e usam essas mensagens há anos: ligar para todo
//   mundo de uma vez mudaria o texto que 300 imobiliárias recebem no WhatsApp sem ninguém
//   ter pedido. Mesmo padrão de utils/logoTenant.ts.
//
// FASE 2 (rollout global): validado o piloto, basta `SUFIXO_GLOBAL_ATIVO = true` — UMA linha,
//   UM deploy, e o recurso passa a valer para todos os sites. A allowlist continua aqui e
//   deixa de importar (o OU curto-circuita); para voltar atrás é a mesma linha de novo.
//
// ⚠️ Ficam de fora POR NATUREZA os tenants com redirecionador externo — 2524 (onpromedia,
// utils/site2524WhatsApp.ts) e 2451 (tintim.link, também no FAB de utils/floatingWhatsHref.ts).
// Não é exceção configurada aqui: o href deles não é api.whatsapp.com/wa.me, então não casa o
// matcher `ehLinkDeWhatsapp` e volta intacto. Neles a mensagem mora na config do
// redirecionador, não no nosso código — mesmo na fase 2 o sufixo não vai aparecer.
const SUFIXO_GLOBAL_ATIVO = false;

export const WHATS_COM_URL_DA_PAGINA = new Set([2463, 1722]);

// SÓ NA PÁGINA DE EMPREENDIMENTO (sugestão #209, cs1832): o lead que chama pelo WhatsApp a
// partir de /empreendimento/<id> chegava sem dizer qual empreendimento era. O recurso acima
// passa a valer nessa rota para estes codsites — no resto do site deles a mensagem segue a de
// hoje. Mesmo gate por codsite da FASE 1; para todos os sites = decisão do Daniel.
export const WHATS_COM_URL_NA_PAGINA_EMPREENDIMENTO = new Set([1832]);
const RE_ROTA_EMPREENDIMENTO = /^\/empreendimento\//;

// `rota` = router.pathname (ex.: "/empreendimento/[id]").
export const siteAnexaUrlDaPaginaNoWhats = (site: any, rota?: string): boolean =>
    SUFIXO_GLOBAL_ATIVO ||
    WHATS_COM_URL_DA_PAGINA.has(Number(site?.id)) ||
    (WHATS_COM_URL_NA_PAGINA_EMPREENDIMENTO.has(Number(site?.id)) && RE_ROTA_EMPREENDIMENTO.test(String(rota || '')));

// Rótulo aprovado pelo cliente. É TAMBÉM o marcador de idempotência: se a mensagem já
// termina com ele, o sufixo antigo é RECORTADO e reescrito com a URL atual — o visitante
// pode ter mudado os filtros entre um clique e outro (router.push shallow não re-renderiza
// o link, então o href reescrito no DOM ficaria "grudado" na URL do primeiro clique).
const ROTULO = 'Página que estou vendo:';
const RE_SUFIXO_JA_ANEXADO = /(?:\n\n)?Página que estou vendo:[\s\S]*$/;

// Só os hosts que aceitam ?text=. chat.whatsapp.com (convite de grupo) fica de fora
// de propósito: não tem mensagem pré-preenchida.
const RE_HOST_WHATS = /^(?:https?:)?\/\/(?:api\.whatsapp\.com|web\.whatsapp\.com|wa\.me)(?:[/?]|$)/i;

// Parâmetros reais do deep link do WhatsApp — servem para saber onde a mensagem TERMINA
// quando (raro) vier algo depois do text=. Nos pontos do projeto o text= é sempre o
// último parâmetro, então isto é cinto de segurança.
export const RE_OUTRO_PARAM = /&(?:phone|app_absent|type|source|data|lang|text)=/i;

// Tem DESTINATÁRIO? "send?phone=55…" ou "wa.me/55…" = mensagem endereçada à imobiliária.
// Sem isso o link é de COMPARTILHAR (send?text=… puro / wa.me/?text=…), e aí o texto é do
// visitante para um amigo dele. Ver a trava em anexaUrlDaPaginaNoWhats.
export const RE_TEM_DESTINATARIO = /[?&]phone=|wa\.me\/\+?\d/i;

export const ehLinkDeWhatsapp = (href: unknown): href is string =>
    typeof href === 'string' && RE_HOST_WHATS.test(href.trim());

// Link de WhatsApp ENDEREÇADO à imobiliária — o de compartilhar com um amigo fica de fora.
export const ehWhatsComDestinatario = (href: unknown): href is string =>
    ehLinkDeWhatsapp(href) && RE_TEM_DESTINATARIO.test(href);

/**
 * Decodifica percent-encoding sem NUNCA lançar exceção.
 *
 * Necessário porque os ~40 pontos que montam link de WhatsApp no projeto misturam três
 * formatos no mesmo text=:
 *   - cru      → "Olá, estou visitando o site da Aoni Imóveis"   (a maioria)
 *   - encodado → "Ol%C3%A1%20tudo%20bem"                         (decorador, href lido do DOM)
 *   - híbrido  → "Olá João!%0A%0AAcabei de acessar"              (CardManager1)
 * E ainda pode haver "%" solto e legítimo ("desconto de 50%"), que faz decodeURIComponent
 * estourar — aí decodificamos só as sequências %XX válidas, uma a uma.
 */
export const decodificaSuave = (valor: string): string => {
    try {
        return decodeURIComponent(valor);
    } catch {
        /* tem % solto — cai no modo por trecho */
    }
    return valor.replace(/(?:%[0-9A-Fa-f]{2})+/g, (trecho) => {
        try {
            return decodeURIComponent(trecho);
        } catch {
            return trecho;
        }
    });
};

/**
 * Devolve o link de WhatsApp com a URL da página anexada ao fim da mensagem.
 *
 * - link que não é WhatsApp (tel:, "#", tintim.link, onpromedia) volta INTACTO;
 * - link de COMPARTILHAR (sem destinatário) também volta INTACTO;
 * - link sem text= ganha um text= só com a URL;
 * - IDEMPOTENTE: rodar 2x, 10x, sempre dá o mesmo resultado (recorta o sufixo anterior);
 * - re-encoda com encodeURIComponent. NÃO usar URLSearchParams.toString(): ele serializa
 *   espaço como "+" (form-urlencoded) e o WhatsApp mostra o "+" LITERAL na mensagem;
 * - manipulação de string pura, sem new URL(): há href malformado em produção
 *   (phone=+5547991389248} com "}" sobrando) e new URL lançaria, quebrando o clique.
 */
export function anexaUrlDaPaginaNoWhats(href: string, urlDaPagina: string): string {
    if (!ehLinkDeWhatsapp(href) || !urlDaPagina) return href;

    // Link de COMPARTILHAR (send?text= sem destinatário): o visitante manda a página pra um
    // amigo, não pra imobiliária — sufixo aqui vazaria a navegação dele pra terceiros.
    // Casos reais: FavoritosComponent, share do blog e o share da página do imóvel
    // (DetailsPiecesProperties), todos "api.whatsapp.com/send?text=<url>" sem phone=.
    if (!RE_TEM_DESTINATARIO.test(href)) return href;

    // Sem text= no link (ex.: MegaFooter, CardManager): cria o parâmetro vazio e segue pelo
    // mesmo caminho — assim a idempotência vale para os dois casos.
    let alvo = href;
    let marca = /([?&])text=/i.exec(alvo);
    if (!marca) {
        alvo = `${alvo}${alvo.includes('?') ? '&' : '?'}text=`;
        marca = /([?&])text=/i.exec(alvo) as RegExpExecArray;
    }

    const inicioDoValor = marca.index + marca[0].length;
    const cabeca = alvo.slice(0, inicioDoValor); // "…/send?phone=55…&text="

    // Tudo depois de "text=" é a mensagem: os "&", "?" e "#" que aparecem ali são do TEXTO
    // ("Silva & Cia", "Pode me ajudar?", "ref #123"), não parâmetros — hoje eles truncam a
    // mensagem no WhatsApp, e reencodar conserta isso de brinde.
    let cauda = alvo.slice(inicioDoValor);
    let resto = '';
    const corte = cauda.search(RE_OUTRO_PARAM);
    if (corte >= 0) {
        resto = cauda.slice(corte);
        cauda = cauda.slice(0, corte);
    }

    const mensagem = decodificaSuave(cauda).replace(RE_SUFIXO_JA_ANEXADO, '');
    const sufixo = `${ROTULO} ${urlDaPagina}`;
    const nova = mensagem ? `${mensagem}\n\n${sufixo}` : sufixo;

    return `${cabeca}${encodeURIComponent(nova)}${resto}`;
}
